import { useState, useEffect, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, CheckCircle } from "@phosphor-icons/react";
import { useI18n } from "@/i18n";
import { setOnboardingCompleted } from "@/lib/history";

// Ordered spotlight steps mapped to live workspace elements.
// mode: "click" advances only when the user clicks a matching element; "next" shows a Next button.
const STEPS = [
  { spotlight: '[data-testid="engine-toggle"]', advance: ['[data-testid="engine-toggle"]'], mode: "click" },
  { spotlight: '[data-testid="privacy-toolbar"]', mode: "next" },
  { spotlight: '[data-testid="load-sample-btn"]', advance: ['[data-testid^="load-scenario-"]', '[data-testid="load-swift-sample-btn"]'], mode: "click" },
  { spotlight: '[data-testid="analyze-btn"]', advance: ['[data-testid="analyze-btn"]'], mode: "click" },
  { spotlight: '[data-testid="report-result"]', waitFor: '[data-testid="report-result"]', mode: "next" },
  { spotlight: '[data-testid="amendment-btn"]', waitFor: '[data-testid="amendment-btn"]', advance: ['[data-testid="amendment-btn"]'], mode: "click" },
];

const POP_W = 330;

export function InteractiveTour({ open, onClose, onStepChange, sampleLoaded, skipSampleStep }) {
  const { t } = useI18n();
  const [step, setStep] = useState(0);
  const [rect, setRect] = useState(null);
  const scrolledFor = useRef(-1);
  const allTexts = t("tour.steps") || [];
  // When the sample is already loaded (e.g. ?demo=mt700), drop the "Try a Sample" step
  // from both the step definitions and their matching copy so indices stay aligned.
  const defs = skipSampleStep ? STEPS.filter((_, i) => i !== 2) : STEPS;
  const steps = skipSampleStep ? allTexts.filter((_, i) => i !== 2) : allTexts;
  const cur = defs[step];

  // Notify the host whenever the visible step changes (used to auto-populate sample data).
  useEffect(() => { if (open) onStepChange?.(step); }, [open, step, onStepChange]);

  const finish = useCallback((completed) => {
    setOnboardingCompleted(true); // either finishing or skipping stops future auto-launch
    setStep(0);
    scrolledFor.current = -1;
    onClose?.(completed);
  }, [onClose]);

  const goNext = useCallback(() => {
    setStep((s) => {
      if (s >= defs.length - 1) { finish(true); return 0; }
      return s + 1;
    });
  }, [finish, defs.length]);

  const goPrev = useCallback(() => {
    setStep((s) => Math.max(0, s - 1));
  }, []);

  // Reset to step 1 whenever the tour (re)opens.
  useEffect(() => { if (open) { setStep(0); scrolledFor.current = -1; } }, [open]);

  // Poll the target element position (also handles scroll/resize/late-rendered nodes).
  useEffect(() => {
    if (!open) return;
    let raf;
    const tick = () => {
      const el = document.querySelector(cur.spotlight);
      if (el) {
        const r = el.getBoundingClientRect();
        setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
        if (scrolledFor.current !== step) {
          scrolledFor.current = step;
          el.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        setRect(null);
      }
    };
    tick();
    const id = setInterval(tick, 200);
    const onScroll = () => (raf = requestAnimationFrame(tick));
    window.addEventListener("scroll", onScroll, true);
    window.addEventListener("resize", onScroll);
    return () => {
      clearInterval(id);
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll, true);
      window.removeEventListener("resize", onScroll);
    };
  }, [open, step, cur.spotlight]);

  // Optional convenience: also advance when the user actually clicks the highlighted control.
  useEffect(() => {
    if (!open || !cur.advance) return;
    const sels = cur.advance;
    let fired = false;
    const handler = (e) => {
      if (fired) return;
      if (sels.some((s) => e.target.closest && e.target.closest(s))) {
        fired = true;
        setTimeout(goNext, 300); // let the app process the click/selection first
      }
    };
    document.addEventListener("pointerdown", handler, true);
    document.addEventListener("click", handler, true);
    return () => {
      document.removeEventListener("pointerdown", handler, true);
      document.removeEventListener("click", handler, true);
    };
  }, [open, step, cur, goNext]);

  if (!open) return null;

  const pad = 6;
  const box = rect
    ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 }
    : null;

  // Popover placement: prefer right of the target (keeps downward dropdowns clickable),
  // then below, then above; fall back to centered when the target isn't found yet.
  let popStyle;
  if (box) {
    const estH = 190;
    const fitsRight = box.left + box.width + 12 + POP_W < window.innerWidth;
    const fitsBelow = box.top + box.height + 12 + estH < window.innerHeight;
    if (fitsRight) {
      const top = Math.min(Math.max(12, box.top), window.innerHeight - estH - 12);
      popStyle = { top, left: box.left + box.width + 12, width: POP_W };
    } else if (fitsBelow) {
      const left = Math.min(Math.max(12, box.left), window.innerWidth - POP_W - 12);
      popStyle = { top: box.top + box.height + 12, left, width: POP_W };
    } else {
      const left = Math.min(Math.max(12, box.left), window.innerWidth - POP_W - 12);
      popStyle = { top: Math.max(12, box.top - 12 - estH), left, width: POP_W };
    }
  } else {
    popStyle = { top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: POP_W };
  }
  if (popStyle) {
    if (typeof popStyle.left === "number") {
      const w = Math.min(POP_W, window.innerWidth - 32);
      popStyle.left = Math.max(16, Math.min(popStyle.left, window.innerWidth - 16 - w));
    }
    popStyle.maxWidth = "calc(100vw - 32px)";
    popStyle.maxHeight = "calc(100vh - 32px)";
    popStyle.overflowY = "auto";
  }
  // Small screens: anchor to the top, full width minus 32px, and scroll internally
  // so the popover body and its Next button always stay inside the viewport.
  if (popStyle && window.innerWidth < 640) {
    popStyle = {
      top: 16, left: 16, right: 16,
      maxWidth: "calc(100vw - 32px)",
      maxHeight: "calc(100vh - 32px)",
      overflowY: "auto",
    };
  }

  const s = steps[step] || {};
  const isSampleStep = cur?.spotlight === '[data-testid="load-sample-btn"]';
  const body = isSampleStep && sampleLoaded ? t("tour.sampleLoaded") : s.body;
  const last = step === defs.length - 1;

  return createPortal(
    <div className="fixed inset-0 z-[95]" data-testid="interactive-tour" style={{ pointerEvents: "none" }}>
      {/* Spotlight hole (dims everything else via a huge box-shadow; clicks pass through). */}
      <AnimatePresence>
        {box && (
          <motion.div
            key="spot"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            style={{
              position: "fixed", top: box.top, left: box.left, width: box.width, height: box.height,
              borderRadius: 8, boxShadow: "0 0 0 9999px rgba(11,17,32,0.78)",
              outline: "2px solid #38BDF8", outlineOffset: 2, pointerEvents: "none",
            }}
          />
        )}
      </AnimatePresence>
      {!box && <div className="absolute inset-0" style={{ background: "rgba(11,17,32,0.78)" }} />}

      {/* Prompt popover */}
      <motion.div
        key={step}
        initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed rounded-md border border-lc-ocean/40 bg-white p-4 shadow-2xl"
        style={{ ...popStyle, pointerEvents: "auto" }}
        data-testid="tour-popover"
      >
        <div className="flex items-center justify-between">
          <span className="font-mono text-[10px] uppercase tracking-widest text-lc-ocean">
            {(t("tour.step") || "Step {n} / {total}").replace("{n}", step + 1).replace("{total}", defs.length)}
          </span>
          <button onClick={() => finish(false)} data-testid="tour-skip"
            style={{ minWidth: 44, minHeight: 44 }}
            className="inline-flex items-center justify-center gap-1 text-[11px] font-semibold text-lc-steel hover:text-lc-ink transition-colors duration-200">
            {t("tour.skip")} <X size={12} />
          </button>
        </div>

        <h3 className="mt-2 font-serif text-lg text-lc-ink" data-testid="tour-title">{s.title}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-lc-steel" data-testid="tour-body">{body}</p>

        <div className="mt-4 flex items-center justify-between gap-2">
          <div className="flex gap-1">
            {defs.map((_, i) => (
              <span key={i} className="h-1.5 w-1.5 rounded-full transition-colors duration-200"
                style={{ background: i <= step ? "#0EA5E9" : "#E2E8F0" }} />
            ))}
          </div>
          <div className="flex items-center gap-2">
            {step > 0 && (
              <button onClick={goPrev} data-testid="tour-prev"
                className="inline-flex items-center gap-1.5 rounded-sm border border-lc-ink/15 px-3 py-2 text-xs font-semibold text-lc-ink hover:bg-lc-paper transition-colors duration-200">
                <ArrowLeft size={13} weight="bold" /> {t("tour.prev")}
              </button>
            )}
            <button onClick={goNext} data-testid="tour-next"
              className="inline-flex items-center gap-1.5 rounded-sm bg-lc-navy px-4 py-2 text-xs font-semibold text-white hover:bg-lc-ocean transition-colors duration-200">
              {last ? <>{t("tour.finish")} <CheckCircle size={14} weight="fill" /></> : <>{t("tour.next")} <ArrowRight size={13} weight="bold" /></>}
            </button>
          </div>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
